import { useEffect, type ReactNode } from 'react';
import { IconEyeOff } from '@tabler/icons-react';
import { IconButton, Progress, Stack, Text, WP_ICON_NAV } from '@wapharma/design-system';
import { useCatalogImageResolveInboxContextOptional } from '../contexts/CatalogImageResolveInboxContext';
import { useToast } from '../contexts/ToastContext';
import type { ImageResolveJobStatus } from '../hooks/useCatalogImageResolveJobInbox';
import { InboxDrillableHint } from './InboxDrillableHint';
import { ImportJobStatusDot } from './ImportJobStatusDot';
import styles from './InboxBulkImportItems.module.css';

function InboxImageResolveCard({
  title,
  subtitle,
  status,
  unread,
  children,
  onDismiss,
  dismissDisabled,
  onOpen,
}: {
  title: string;
  subtitle?: string;
  status: ImageResolveJobStatus;
  unread?: boolean;
  children?: ReactNode;
  onDismiss?: () => void;
  dismissDisabled?: boolean;
  onOpen?: () => void;
}) {
  return (
    <article
      className={`${styles.card} ${unread ? styles.cardUnread : styles.cardRead}`}
      data-inbox-drillable={onOpen ? '' : undefined}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      style={onOpen ? { cursor: 'pointer' } : undefined}
    >
      <div className={styles.cardHead}>
        <ImportJobStatusDot status={status} size={12} className={styles.cardStatus} />
        <div className={styles.cardTitles}>
          <Text size="sm" fw={unread ? 600 : 500} style={{ lineHeight: 1.35 }}>
            {title}
          </Text>
          {subtitle ? (
            <Text size="xs" c="dimmed" style={{ marginTop: 4 }}>
              {subtitle}
            </Text>
          ) : null}
        </div>
        <div className={styles.cardActionsInline}>
          {onOpen ? <InboxDrillableHint /> : null}
          {onDismiss || dismissDisabled ? (
            <IconButton
              type="button"
              size="xs"
              variant="ghost"
              aria-label={dismissDisabled ? 'Masquer indisponible pendant la résolution' : 'Masquer'}
              title={dismissDisabled ? 'Résolution en cours — masquage indisponible' : undefined}
              disabled={dismissDisabled}
              onClick={
                dismissDisabled
                  ? undefined
                  : (e) => {
                      e.stopPropagation();
                      onDismiss?.();
                    }
              }
            >
              <IconEyeOff {...WP_ICON_NAV} />
            </IconButton>
          ) : null}
        </div>
      </div>
      {children ? <div className={styles.cardBody}>{children}</div> : null}
    </article>
  );
}

export function InboxBulkImageResolveItems({
  onOpenJob,
  onDismissJob,
  isJobSeen,
  onMarkJobSeen,
}: {
  onOpenJob?: (jobId: string) => void;
  onDismissJob?: (jobId: string, status: ImageResolveJobStatus) => void;
  isJobSeen?: (jobId: string, status: ImageResolveJobStatus) => boolean;
  onMarkJobSeen?: (jobId: string, status: ImageResolveJobStatus) => void | Promise<void>;
}) {
  const { showToast } = useToast();
  const inbox = useCatalogImageResolveInboxContextOptional();
  if (!inbox) return null;

  const { job, err, inFlight, showResult, imageResolveProgress } = inbox;
  const openJobId = job?.jobId;
  const openDetail =
    openJobId && onOpenJob
      ? () => {
          if (job) void onMarkJobSeen?.(openJobId, job.status);
          onOpenJob(openJobId);
        }
      : undefined;

  const showProgress = Boolean(imageResolveProgress && inFlight);
  const showErr = Boolean(err && (showProgress || showResult));

  useEffect(() => {
    if (showErr && err) showToast(err, { tone: 'error' });
  }, [showErr, err, showToast]);

  if (!showProgress && !showResult && !showErr) return null;

  const progressStatus: ImageResolveJobStatus =
    job?.status === 'PENDING' || job?.status === 'RUNNING' ? job.status : 'RUNNING';

  const progressUnread =
    openJobId && isJobSeen ? !isJobSeen(openJobId, progressStatus) : true;

  const resultUnread =
    openJobId && isJobSeen && job ? !isJobSeen(openJobId, 'COMPLETED') : true;

  const dismissJob = (status: ImageResolveJobStatus) => {
    if (openJobId) {
      void onMarkJobSeen?.(openJobId, status);
      onDismissJob?.(openJobId, status);
    }
    inbox.dismissResult();
  };

  return (
    <Stack gap="xs" className={styles.list} aria-label="Résolution images catalogue">
      {showProgress && imageResolveProgress ? (
        <InboxImageResolveCard
          title="Résolution images en cours"
          status={progressStatus}
          unread={progressUnread}
          subtitle={
            job?.label?.trim() ??
            `${imageResolveProgress.current.toLocaleString('fr-FR')} / ${imageResolveProgress.total.toLocaleString('fr-FR')}`
          }
          onOpen={openDetail}
          onDismiss={() => dismissJob(progressStatus)}
          dismissDisabled
        >
          <Progress
            value={imageResolveProgress.current}
            max={imageResolveProgress.total || 1}
            tone="neutral"
            size="md"
            className={styles.importProgress}
          />
        </InboxImageResolveCard>
      ) : null}

      {showResult && job ? (
        <InboxImageResolveCard
          title="Résolution images terminée"
          status="COMPLETED"
          unread={resultUnread}
          subtitle={job.label ?? undefined}
          onOpen={openDetail}
          onDismiss={() => dismissJob('COMPLETED')}
        >
          <Text size="xs" c="dimmed">
            {job.readyCount.toLocaleString('fr-FR')} prêtes · {job.reviewCount.toLocaleString('fr-FR')} à valider ·{' '}
            {job.failedCount.toLocaleString('fr-FR')} échouées
          </Text>
        </InboxImageResolveCard>
      ) : null}
    </Stack>
  );
}
